import { formatDistanceToNowStrict } from 'date-fns';
import i18next from 'i18next';

import { az, enUS, es, fr, nl, pl, ru, tr, uk, vi } from 'date-fns/locale';
import {
  isBrowserSupportRelativeDateFormat,
  DEFAULT_AMOUNT_PRECISION,
} from '@make-software/cspr-design';
import { createIntl, createIntlCache } from '@formatjs/intl';
import Big from "big.js";

const Locale = {
  en: enUS,
  es: es,
  uk: uk,
  tr: tr,
  nl: nl,
  ru: ru,
  pl: pl,
  vi: vi,
  fr: fr,
  az: az,
};

const cache = createIntlCache();

const intl = createIntl(
  {
    locale: 'en-US',
    messages: {
      'components.transaction_status': 'Success',
    },
  },
  cache,
);

export const formatNumber = (
    value: number | string,
    {
      precision,
      notation,
      compactDisplay,
    }: {
      precision?: number;
      notation?: 'compact' | 'standard';
      compactDisplay?: 'short' | 'long';
    } = {},
): string => {
  return intl.formatNumber(value as number, {
    minimumFractionDigits: precision || 0,
    maximumFractionDigits: precision || 0,
    notation,
    compactDisplay,
  });
};

export const formatCurrency = (
    value: number | string,
    code: string,
    {
      precision,
    }: {
      precision?: number;
    } = {},
): string => {
  const amountPrecision = precision ?? DEFAULT_AMOUNT_PRECISION;
  const displayThresholdValue = Big(1).div(Big(10).pow(amountPrecision));
  const isAmountBelowDisplayThreshold = Big(value).gt(0) &&
      Big(value).lt(displayThresholdValue);

  if (isAmountBelowDisplayThreshold) {
    return `< ${intl.formatNumber(displayThresholdValue.toNumber(), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: amountPrecision,
      maximumFractionDigits: amountPrecision,
    })}`;
  }

  return intl.formatNumber(value as number, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

export const formatTimestamp = (value: string): string => {
  const date = new Date(value);
  const locale = i18next.language || 'en';
  const nativeIntl = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
  });

  return `${nativeIntl.format(date)}`;
};

const formatDistanceTokens = {
  lessThanXSeconds: 'second',
  xSeconds: 'second',
  lessThanXMinutes: 'minute',
  xMinutes: 'minute',
  xHours: 'hour',
  xDays: 'day',
  xMonths: 'month',
  xYears: 'year',
};

const formatDistance = (token, count, options) => {
  options = options || {};
  const locale = options.locale.code || 'en';

  return new (Intl as any).RelativeTimeFormat(locale, {
    style: 'short',
  })
    .format(-count, formatDistanceTokens[token])
    .replace('.', '');
};

export const formatTimestampAge = (value: string): string => {
  const date = new Date(value);
  const isSupportedLanguageAndBrowser =
    i18next.language !== 'az' && isBrowserSupportRelativeDateFormat;

  return `${formatDistanceToNowStrict(date, {
    addSuffix: true,
    locale: {
      ...Locale[i18next.language],
      ...(isSupportedLanguageAndBrowser && {
        formatDistance,
      }),
    },
    roundingMethod: 'floor',
  })}`;
};

export const formatDate = (value: string | number): string => {
  const date = new Date(value);
  const locale = i18next.language || 'en';

  return `${new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)}, ${new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
  }).format(date)}`;
};

export const formatDateWithWeekday = (value: string): string => {
  const date = new Date(value);
  const locale = i18next.language || 'en';
  return `${new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)}`;
};

export const formatDateShort = (value: string): string => {
  const date = new Date(value);
  const locale = i18next.language || 'en';

  return `${new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(date)}`;
};

export const formatDateWithMonthAndYear = (value: string): string => {
  const date = new Date(value);
  const locale = i18next.language || 'en';
  return `${new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric',
  }).format(date)}`;
};

export enum HashLength {
  FULL = 0,
  TINY = 5,
  LITTLE = 10,
  SMALL = 15,
  MEDIUM = 20,
  LARGE = 25,
}
